/**
 * Message Router for consolidated WebSocket system
 * Handles routing messages to appropriate handlers with priority and deduplication
 */

import { WebSocketMessage, MessageHandler } from './types';

export class WebSocketMessageRouter {
  private handlers: Map<string, MessageHandler[]> = new Map();
  private deduplicatedMessages: Set<string> = new Set();
  private messageTimeout = 5000; // 5 seconds
  
  // Register a message handler
  registerHandler(handler: MessageHandler): void {
    const { messageType } = handler;
    
    if (!this.handlers.has(messageType)) {
      this.handlers.set(messageType, []);
    }
    
    this.handlers.get(messageType)!.push(handler);
    
    // Sort handlers by priority (higher numbers first)
    this.handlers.get(messageType)!.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    console.log(`🔗 Registered handler for ${messageType} from service ${handler.service} with priority ${handler.priority || 0}`);
  }
  
  // Unregister a handler
  unregisterHandler(messageType: string, service: string): void {
    const handlers = this.handlers.get(messageType);
    if (handlers) {
      const filtered = handlers.filter(h => h.service !== service);
      
      if (filtered.length === 0) {
        this.handlers.delete(messageType);
      } else {
        this.handlers.set(messageType, filtered);
      }
      
      console.log(`🔗 Unregistered handler for ${messageType} from service ${service}`);
    }
  }
  
  // Route message to appropriate handlers
  routeMessage(message: WebSocketMessage): void {
    const { type } = message;
    
    if (!type) {
      console.warn('❌ WebSocketMessageRouter - Message missing type:', message);
      return;
    }
    
    // Create deduplication key
    const messageKey = this.createMessageKey(message);
    
    // Check for duplicate messages
    if (this.isDuplicate(messageKey)) {
      console.log('🔄 WebSocketMessageRouter - Skipping duplicate message:', { type, messageKey });
      return;
    }
    
    // Mark message as processed
    this.markAsProcessed(messageKey);
    
    // Get handlers for this message type
    const handlers = this.handlers.get(type);
    if (!handlers || handlers.length === 0) {
      console.log('❌ WebSocketMessageRouter - No handlers registered for message type:', type);
      console.log('🔍 WebSocketMessageRouter - Available message types:', Array.from(this.handlers.keys()));
      return;
    }
    
    console.log(`🔄 WebSocketMessageRouter - Routing ${type} message to ${handlers.length} handlers`);
    
    // Route to all handlers for this message type
    handlers.forEach(handler => {
      try {
        console.log(`🔄 WebSocketMessageRouter - Executing handler for ${handler.service}`);
        handler.handler(message);
      } catch (error) {
        console.error(`❌ Error in handler for ${handler.service}:`, error, message);
      }
    });
  }
  
  // Clear all handlers (for cleanup)
  clearHandlers(): void {
    this.handlers.clear();
    this.deduplicatedMessages.clear();
    console.log('🧹 WebSocketMessageRouter - All handlers cleared');
  }
  
  // Get handlers for a message type (for debugging)
  getHandlers(messageType: string): MessageHandler[] {
    return this.handlers.get(messageType) || [];
  }
  
  // Get all registered message types
  getRegisteredMessageTypes(): string[] {
    return Array.from(this.handlers.keys());
  }
  
  // Create deduplication key for message
  private createMessageKey(message: WebSocketMessage): string {
    const { type, data, timestamp } = message;
    
    if (type === 'valve_command_update') {
      // For valve command updates, create a more specific key
      const deviceId = data?.device_id || data?.dev_eui || 'unknown';
      const valveNumber = data?.valve_number || 'unknown';
      const commandId = data?.command_id || 'no-command';
      const state = data?.new_state || 'no-state';
      return `valve_cmd-${commandId}-${deviceId}-${valveNumber}-${state}`;
    }
    
    // For other message types, use a simpler key
    const identifier = data?.device_id || data?.nodeId || data?.dev_eui || 'no-id';
    return `${type}-${identifier}-${timestamp || Date.now()}`;
  }
  
  // Check if message is duplicate
  private isDuplicate(messageKey: string): boolean {
    return this.deduplicatedMessages.has(messageKey);
  }
  
  // Mark message as processed
  private markAsProcessed(messageKey: string): void {
    this.deduplicatedMessages.add(messageKey);
    
    // Clean up after timeout
    setTimeout(() => {
      this.deduplicatedMessages.delete(messageKey);
    }, this.messageTimeout);
  }
  
  // Set message timeout for deduplication
  setMessageTimeout(timeoutMs: number): void {
    this.messageTimeout = timeoutMs;
  }
  
  // Get statistics
  getStatistics() {
    return {
      registeredMessageTypes: this.handlers.size,
      totalHandlers: Array.from(this.handlers.values()).reduce((sum, handlers) => sum + handlers.length, 0),
      deduplicatedMessagesCount: this.deduplicatedMessages.size,
      messageTypes: Array.from(this.handlers.keys())
    };
  }
}